import { Head, usePage, router } from "@inertiajs/react"
import { useState, useCallback, useEffect } from "react"
import { track } from "@/lib/analytics"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import type { Account, CreditTransaction, SharedProps, Plan, Subscription } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import { CreditRing } from "@/components/dashboard/credit-indicator"
import { useAccountChannel } from "@/hooks/use-account-channel"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { csrfToken } from "@/lib/csrf"

interface DailyUsage {
  date: string
  credits: number
}

const TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "deduction", label: "Deductions" },
  { value: "topup", label: "Top-ups" },
  { value: "adjustment", label: "Adjustments" },
] as const

const TYPE_BADGE: Record<string, { bg: string; label: string }> = {
  topup: { bg: "border border-red/20 bg-red/5 text-red", label: "Top-up" },
  deduction: { bg: "border border-charcoal/15 bg-charcoal/5 text-charcoal", label: "Deduction" },
  adjustment: { bg: "border border-stone/20 bg-stone/10 text-warm-grey", label: "Adjustment" },
}

export default function CreditsShow({
  account,
  transactions,
  used_today,
  used_this_month,
  daily_average,
  daily_chart,
  type_filter,
  plans,
  has_stripe_customer,
  subscription,
}: {
  account: Account
  transactions: CreditTransaction[]
  used_today: number
  used_this_month: number
  daily_average: number
  daily_chart: DailyUsage[]
  type_filter: string | null
  plans: Plan[]
  has_stripe_customer: boolean
  subscription: Subscription | null
}) {
  const { currentAccount } = usePage<SharedProps>().props
  const [liveBalance, setLiveBalance] = useState<number | null>(null)
  const [showBuyDialog, setShowBuyDialog] = useState(false)
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null)
  const [loadingPortal, setLoadingPortal] = useState(false)

  useAccountChannel(
    currentAccount?.id,
    useCallback((balance: number) => setLiveBalance(balance), []),
  )

  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    if (p.get("checkout") === "success") {
      toast.success("Payment successful — credits will appear in your account shortly.")
      window.history.replaceState({}, "", "/dashboard/credits")
    }
    if (p.get("checkout") === "cancelled") {
      window.history.replaceState({}, "", "/dashboard/credits")
    }
  }, [])

  const balance = liveBalance ?? account.credit_balance
  const peak = account.peak_credit_balance
  const percentage = peak > 0
    ? Math.max(0, Math.min(100, (balance / peak) * 100))
    : balance > 0 ? 100 : 0
  const isLow = peak > 0 && balance / peak < 0.15
  const displayBalance = balance < 0 ? 0 : balance

  function handleFilter(value: string) {
    const params: Record<string, string> = {}
    if (value !== "all") params.type = value
    router.get("/dashboard/credits", params, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  async function handleBuy(planId: number) {
    setLoadingPlanId(planId)
    try {
      const res = await fetch("/dashboard/checkout_sessions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ plan_id: planId }),
      })
      const { url, error } = await res.json() as { url?: string; error?: string }
      if (url) {
        const plan = plans.find((p) => p.id === planId)
        track("credits_plan_purchased", {
          plan_id: planId,
          plan_name: plan?.name,
          plan_kind: plan?.kind,
          credits: plan?.credits,
        })
        window.location.href = url
      } else {
        toast.error(error ?? "Something went wrong")
      }
    } catch {
      toast.error("Something went wrong")
    } finally {
      setLoadingPlanId(null)
    }
  }

  async function handleManageSubscription() {
    setLoadingPortal(true)
    try {
      const res = await fetch("/dashboard/billing_portal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })
      const { url, error } = await res.json() as { url?: string; error?: string }
      if (url) {
        track("credits_portal_opened")
        window.location.href = url
      } else {
        toast.error(error ?? "Something went wrong")
      }
    } catch {
      toast.error("Something went wrong")
    } finally {
      setLoadingPortal(false)
    }
  }

  const fmt = (n: number) =>
    typeof n === "number" && !Number.isInteger(n)
      ? n.toFixed(1)
      : n.toLocaleString()

  const oneOffPlans = plans.filter((p) => p.kind === "one_off")
  const subscriptionPlans = plans.filter((p) => p.kind === "subscription")

  return (
    <DashboardLayout>
      <Head title="Credits | QuiteRight" />

      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">Credits</h1>
            <p className="mt-1 text-sm text-warm-grey">
              Your credit balance and usage history.
            </p>
          </div>
          <Button
            onClick={() => {
              track("credits_purchase_clicked")
              setShowBuyDialog(true)
            }}
            className="bg-red hover:bg-red/90 text-white"
          >
            Add credits
          </Button>
        </div>

        {/* Stats row */}
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="group relative flex items-center gap-4 overflow-hidden rounded-xl border border-stone/40 bg-white px-5 py-4 transition-colors hover:bg-cream/40">
            <CreditRing
              percentage={percentage}
              label=""
              size={44}
              strokeWidth={3}
              low={isLow}
            />
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-warm-grey">Balance</p>
              <p className="mt-2 text-3xl font-semibold tabular-nums text-charcoal">
                {fmt(displayBalance)}
              </p>
            </div>
            <div className="pointer-events-none absolute -right-3 -top-3 size-16 rounded-full bg-charcoal/[0.03] transition-transform duration-300 group-hover:scale-150" />
          </div>
          <StatCard label="Used today" value={used_today} />
          <StatCard label="Used this month" value={used_this_month} />
          <StatCard label="Daily average (30d)" value={daily_average} />
        </div>

        {/* Subscription status card — only for accounts with a real Stripe subscription */}
        {subscription?.stripe_subscription_id && subscription.status === "active" && (() => {
          const subPlan = plans.find((p) => p.id === subscription.plan_id)
          const planName = subPlan?.name ?? subscription.plan_tier.charAt(0).toUpperCase() + subscription.plan_tier.slice(1)
          return (
          <div className="flex items-center justify-between rounded-xl border border-stone/40 bg-white px-5 py-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-warm-grey">Active subscription</p>
              <p className="mt-0.5 text-sm font-semibold text-charcoal">{planName}</p>
              {subscription.current_period_end && (
                <p className="mt-0.5 text-xs text-warm-grey">
                  Renews {new Date(subscription.current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              )}
            </div>
            {has_stripe_customer && (
              <Button
                variant="outline"
                size="sm"
                className="border-stone/40"
                onClick={handleManageSubscription}
                disabled={loadingPortal}
              >
                {loadingPortal ? "Loading…" : "Manage subscription"}
              </Button>
            )}
          </div>
          )
        })()}

        {/* Usage chart */}
        <div className="rounded-xl border border-stone/40 bg-white p-5">
          <h2 className="mb-4 text-[11px] font-medium uppercase tracking-wider text-warm-grey">
            30-day usage
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily_chart} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                <CartesianGrid stroke="rgba(232,228,223,0.5)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#9A9490", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(val: string, i: number) => {
                    const total = daily_chart.length
                    if (i === 0 || i === total - 1) return val
                    if (total <= 14) return val
                    return i % Math.ceil(total / 10) === 0 ? val : ""
                  }}
                />
                <YAxis
                  tick={{ fill: "#9A9490", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  width={40}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(232,228,223,0.3)" }} />
                <Bar dataKey="credits" fill="#C41E2A" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Transaction history */}
        <div className="rounded-xl border border-stone/40 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-warm-grey">
              Transactions
              <span className="ml-1.5 text-warm-grey/50">({transactions.length})</span>
            </h2>
            <Select
              value={type_filter || "all"}
              onValueChange={handleFilter}
            >
              <SelectTrigger className="h-8 w-[140px] border-stone/40 bg-cream/50 text-xs text-charcoal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_FILTERS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {transactions.length === 0 ? (
            <EmptyState filtered={!!type_filter} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone/30 text-left text-[11px] uppercase tracking-wider text-warm-grey">
                    <th className="pb-2.5 pr-4 font-medium">Date</th>
                    <th className="pb-2.5 pr-4 font-medium">Type</th>
                    <th className="pb-2.5 pr-4 font-medium">Description</th>
                    <th className="pb-2.5 pr-4 text-right font-medium">Amount</th>
                    <th className="pb-2.5 text-right font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => {
                    const date = new Date(tx.created_at)
                    const isPositive = tx.amount > 0
                    const badge = TYPE_BADGE[tx.transaction_type] || { bg: "bg-stone/20 text-charcoal", label: tx.transaction_type }

                    return (
                      <tr key={tx.id} className="border-b border-stone/15 last:border-0">
                        <td className="whitespace-nowrap py-2 pr-4 text-xs tabular-nums text-warm-grey">
                          {date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}{" "}
                          <span className="text-warm-grey/50">
                            {date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </td>
                        <td className="whitespace-nowrap py-2 pr-4">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.bg}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="max-w-[280px] truncate py-2 pr-4 text-xs text-charcoal/70">
                          {tx.description || "\u2014"}
                        </td>
                        <td className={`whitespace-nowrap py-2 pr-4 text-right text-xs tabular-nums font-medium ${isPositive ? "text-red" : "text-charcoal"}`}>
                          {isPositive ? "+" : ""}{tx.amount.toLocaleString()}
                        </td>
                        <td className="whitespace-nowrap py-2 text-right text-xs tabular-nums text-warm-grey">
                          {tx.balance_after.toLocaleString()}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Purchase dialog */}
      <Dialog open={showBuyDialog} onOpenChange={setShowBuyDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Add credits</DialogTitle>
            <DialogDescription className="text-warm-grey">
              Choose a credit pack for one-off top-ups, or a subscription for regular credits each month.
            </DialogDescription>
          </DialogHeader>

          {plans.length === 0 ? (
            <p className="py-4 text-sm text-warm-grey">No plans available at the moment. Please contact us.</p>
          ) : (
            <Tabs defaultValue="packs" className="mt-2">
              <TabsList className="w-full">
                <TabsTrigger value="packs" className="flex-1">Credit packs</TabsTrigger>
                <TabsTrigger value="subscription" className="flex-1">Subscription</TabsTrigger>
              </TabsList>

              <TabsContent value="packs" className="mt-4 space-y-2">
                {oneOffPlans.length === 0 ? (
                  <p className="py-4 text-center text-sm text-warm-grey">No credit packs available.</p>
                ) : (
                  oneOffPlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      loading={loadingPlanId === plan.id}
                      onBuy={() => handleBuy(plan.id)}
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="subscription" className="mt-4 space-y-2">
                {subscriptionPlans.length === 0 ? (
                  <p className="py-4 text-center text-sm text-warm-grey">No subscription plans available.</p>
                ) : (
                  subscriptionPlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      loading={loadingPlanId === plan.id}
                      onBuy={() => handleBuy(plan.id)}
                    />
                  ))
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}

function PlanCard({
  plan,
  loading,
  onBuy,
}: {
  plan: Plan
  loading: boolean
  onBuy: () => void
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-stone/40 bg-cream/30 px-4 py-3">
      <div>
        <p className="text-sm font-semibold text-charcoal">{plan.name}</p>
        <p className="mt-0.5 text-xs text-warm-grey">
          {plan.credits.toLocaleString()} credits
          {plan.kind === "subscription" ? " / month" : ""}
        </p>
      </div>
      <Button
        size="sm"
        className="bg-red hover:bg-red/90 text-white"
        onClick={onBuy}
        disabled={loading}
      >
        {loading ? "Loading…" : "Buy"}
      </Button>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-stone/40 bg-white p-5 transition-colors hover:bg-cream/40">
      <p className="text-xs font-medium uppercase tracking-wider text-warm-grey">{label}</p>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-charcoal">
        {typeof value === "number" && !Number.isInteger(value)
          ? value.toFixed(1)
          : value.toLocaleString()}
      </p>
      <div className="pointer-events-none absolute -right-3 -top-3 size-16 rounded-full bg-charcoal/[0.03] transition-transform duration-300 group-hover:scale-150" />
    </div>
  )
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-lg border border-stone/40 bg-white px-3 py-2 shadow-lg">
      {label && <p className="mb-0.5 text-[11px] text-warm-grey">{label}</p>}
      <p className="text-sm font-medium tabular-nums text-charcoal">
        {payload[0].value.toLocaleString()} credits
      </p>
    </div>
  )
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center py-10 text-center">
      <svg
        className="size-10 text-stone"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
        />
      </svg>
      <p className="mt-3 text-sm font-medium text-charcoal">
        {filtered ? "No matching transactions" : "No transactions yet"}
      </p>
      <p className="mt-1 text-xs text-warm-grey">
        {filtered
          ? "Try a different filter to see more results."
          : "Credit activity will appear here as you create videos."}
      </p>
    </div>
  )
}
